import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from 'react-bootstrap'
import Select from 'react-select'

import { httpOk, POST } from 'helpers/api'
import { formatDate, formatMoney, regexPhoneNumber } from 'helpers/helpers'
import { useSelector } from 'react-redux'

function Return({ showModal, setShowModal, onSuccess }) {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)
	const reduxCashbox = useSelector(state => state.cashbox)

	const [data, setData] = useState({})
	const [returnPaymentTypeId, setReturnPaymentTypeId] = useState(null)
	const [paymentTypeError, setPaymentTypeError] = useState(false)
	const [submitting, setSubmitting] = useState(false) // dublikat qaytarim himoyasi

	function isMetrReturnProduct(product) {
		const productType = Number(product?.product_type ?? product?.product_type_id)
		const isPrepared = product?.is_prepared === true || product?.is_prepared === 1 || product?.is_prepared === '1'
		return [3, 4, 5].includes(productType) && !isPrepared
	}

	function getReturnQuantity(product) {
		if (isMetrReturnProduct(product)) {
			return Number(product?.metr ?? product?.quantity ?? 0)
		}
		return Number(product?.quantity ?? 0)
	}

	// Qaytarish summasi: tunikofon (tur 5) sotuvda sale_price × width × metr (kv_metr) bilan hisoblanadi —
	// qaytarishда ham shunday bo'lishi kerak (aks holda width barobar noto'g'ri). Boshqa turlar: sale_price × miqdor.
	function getReturnAmount(product) {
		const price = Number(product?.sale_price ?? 0)
		const qty = Number(product?.returned_quantity ?? 0)
		const pt = Number(product?.product_type ?? product?.product_type_id)
		const width = Number(product?.width ?? 0)
		const isPrepared = product?.is_prepared === true || product?.is_prepared === 1 || product?.is_prepared === '1'
		if (pt === 5 && !isPrepared && width > 0) {
			return price * width * qty
		}
		return price * qty
	}

	// Naqd qaytarish summasi (qarzdan keyin qolgan)
	// T5: faqat mijoz QARZI (manfiy balans) qaytariladigan summadan ayirilsin; AVANS (musbat balans) naqdga aylanmasin
	const cashToReturn = Math.max(
		Number(data.totalReturnedAmount ?? 0) + Math.min(Number(data.client_balance ?? 0), 0),
		0
	)

	async function createReturn() {
		if (submitting) return // ikki marta bosishda ikkita qaytarim bo'lmasin
		if (cashToReturn > 0 && !returnPaymentTypeId) {
			setPaymentTypeError(true)
			return
		}
		setPaymentTypeError(false)
		setSubmitting(true)

		let dataCopy = structuredClone(data)
		dataCopy.shift_id = reduxAccount.shift?.id

		// Tanlangan to'lov turini payments ga qo'shamiz
		if (returnPaymentTypeId) {
			const selectedType = reduxCashbox?.paymentTypes?.find(p => p.id === returnPaymentTypeId)
			dataCopy.payments = [{
				payment_type_id: returnPaymentTypeId,
				payment_type_category_id: selectedType?.payment_type_category_id ?? 1,
				amount: cashToReturn,
			}]
		} else if (!dataCopy.payments?.length) {
			// Qarz kamaytirilsa ham birinchi payment dan olamiz
			dataCopy.payments = [{ payment_type_id: 1, payment_type_category_id: 1, amount: 0 }]
		}

		try {
			const response = await POST('/cheques-products-return', dataCopy, { loader: true, notification: true })
			if (httpOk(response)) {
				if (onSuccess) onSuccess()
				setShowModal(false)
			}
		} finally {
			setSubmitting(false)
		}
	}

	function handleChange(e, index) {
		let dataCopy = { ...data }
		var product = dataCopy.products[index]
		// T6: faqat yuqori chegara emas — manfiy va harfli (NaN) miqdorni ham bloklaymiz (bo'sh input ruxsat, tozalash uchun)
		const val = Number(e.target.value)
		const limit = getReturnQuantity(product) - Number(product.original_returned_quantity ?? 0)
		if (e.target.value !== '' && (isNaN(val) || val < 0 || val > limit)) {
			return
		}
		product.returned_quantity = e.target.value
		dataCopy.totalReturnedAmount = 0
		for (let i = 0; i < dataCopy.products.length; i++) {
			dataCopy.products[i].totalReturnedAmount = getReturnAmount(dataCopy.products[i])
			dataCopy.totalReturnedAmount += getReturnAmount(dataCopy.products[i])
		}
		setData(dataCopy)
		setPaymentTypeError(false)
	}

	useEffect(() => {
		if (showModal.show) {
			var dataCopy = structuredClone(showModal.cheque)
			dataCopy.payment_type_id = ''
			dataCopy.note = ''
			setData(dataCopy)
			setReturnPaymentTypeId(null)
			setPaymentTypeError(false)
		}
	}, [showModal.show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal
			show={showModal.show} animation={false} centered size="lg"
			onHide={() => setShowModal(false)}>
			<Modal.Header closeButton>
				<h4>
					{t('return')}
				</h4>
			</Modal.Header>
			<Modal.Body>
				<div className="row mb-3">
					<div className="col-6">
						{data.id &&
							<h5>{t('order')} № {data.id}</h5>
						}
						{
							<div className="d-between">
								<div>{t('date')}</div>
								<div>{formatDate(data.cheque_opened_at ? data.cheque_opened_at : new Date(), "dd.MM.yyyy HH:mm")}</div>
							</div>
						}
						<div className="d-between">
							<div>{t('client')}</div>
							<div>{data.client_name}</div>
						</div>
						<div className="d-between">
							<div>{t('phone')}</div>
							<div>{regexPhoneNumber(data.client_phone)}</div>
						</div>
						{data.address &&
							<div className="d-between">
								<div>{t('address')}</div>
								<div>{data.address}</div>
							</div>
						}
						{data.note &&
							<div className="d-between">
								<div>{t('note')}</div>
								<div>{data.note}</div>
							</div>
						}
					</div>

					<div className="col-6">
						<div className="d-flex flex-column justify-content-end h-100">
							<div className="d-between">
								<div>{t('cashier')}</div>
								<div>{data.cashier_name}</div>
							</div>
							<div className="d-between">
								<div>{t('phone')}</div>
								<div>{regexPhoneNumber(data.cashier_phone)}</div>
							</div>
							{data.delivery_type_id &&
								<div className="d-between">
									<div>{t('delivery_type')}</div>
									<div>{data.delivery_type_name}</div>
								</div>
							}
							{data.courier_login &&
								<div className="d-between">
									<div>{t('courier')}</div>
									<div>{data.courier_name}</div>
								</div>
							}
							{data.delivery_date &&
								<div className="d-between">
									<div>{t('delivery_date')}</div>
									<div>{formatDate(data.delivery_date, "dd.MM.yyyy HH:mm")}</div>
								</div>
							}
						</div>
					</div>
				</div>

				<table className="table table-bordered">
					<thead>
						<tr>
							<th style={{ width: '20%' }}>{t('name2')}</th>
							<th style={{ width: '30%' }}>{t('roll')}</th>
							<th style={{ width: '10%' }} className="text-end">{t('sold')}</th>
							<th style={{ width: '10%' }} className="text-end">{t('returned')}</th>
							<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
							<th style={{ width: '20%' }} className="text-end">{t('amount')}</th>
						</tr>
					</thead>
					<tbody>
						{data?.products?.map((item, index) => (
							<tr key={index}>
								<td>{item.product_name}</td>
								<td>{item.element_name}</td>
								<td className="text-end">{formatMoney(getReturnQuantity(item), 3)}</td>
								<td className="text-end">{formatMoney(item.original_returned_quantity, 3)}</td>
								<td className="td-input">
									<input type="text" value={item.returned_quantity} onChange={(e) => handleChange(e, index)} />
								</td>
								<td className="text-end">{formatMoney(getReturnAmount(item))}</td>
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr className="tr-bold">
							<td>{t('total')}</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
							<td className="text-end">{formatMoney(data.totalReturnedAmount)}</td>
						</tr>
					</tfoot>
				</table>

				<div className="row">
					<div className="col-md-6">
						<div className="form-group w-100">
							<label htmlFor="note">{t('note')}</label>
							<textarea type="text" className="form-control" name="note" style={{ minHeight: '100px' }}
								value={data.note} onChange={(e) => setData({ ...data, note: e.target.value })} />
						</div>
					</div>

					<div className="col-md-5 offset-1">
						<div className="d-flex flex-column justify-content-center h-100">
							{data.client_balance < 0 &&
								<>
									<div className="d-between fz-18 text-dark">
										<div>{t('current_debt')}</div>
										<div className="text-end" style={{ width: '130px' }}>
											{formatMoney(Math.abs(Number(data.client_balance)))}
										</div>
									</div>
									<div className="d-between fz-18 text-dark">
										<div>{t('new_debt')}</div>
										<div className="text-end" style={{ width: '130px' }}>
											{Number(data.totalReturnedAmount) + Number(data.client_balance) > 0 ?
												formatMoney(0)
												:
												formatMoney(Math.abs(Number(data.client_balance) + Number(data.totalReturnedAmount)))
											}
										</div>
									</div>
								</>
							}

							{cashToReturn > 0 &&
								<div className="d-between fz-18 text-dark mb-2">
									<div>{t('to_pay')}</div>
									<div className="text-end fw-bold text-danger" style={{ width: '130px' }}>
										{formatMoney(cashToReturn)}
									</div>
								</div>
							}

							{/* To'lov turi — faqat naqd qaytarish kerak bo'lganda */}
							{cashToReturn > 0 &&
								<div className="form-group mt-2">
									<label className={paymentTypeError ? 'text-danger' : ''}>
										{t('payment_type')} {paymentTypeError && <span>*{t('required')}</span>}
									</label>
									<Select
										options={reduxCashbox?.paymentTypes?.filter(p =>
											p.payment_type_category_id === 1 || p.payment_type_category_id === 2
										)}
										value={reduxCashbox?.paymentTypes?.find(p => p.id === returnPaymentTypeId) || null}
										onChange={(option) => {
											setReturnPaymentTypeId(option?.id ?? null)
											setPaymentTypeError(false)
										}}
										placeholder={t('select')}
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(o) => o.name}
										getOptionValue={(o) => o.id}
										className={paymentTypeError ? 'border border-danger rounded' : ''}
									/>
								</div>
							}

							{data?.payments?.map((item, index) => (
								item.amount > 0 &&
								<div className="d-between gap-2 fz-14 mt-1" key={index}>
									<div>{item.name}:</div>
									<div className="text-end" style={{ width: '140px' }}>
										{formatMoney(item.amount)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<div className="d-flex justify-content-end gap-2">
					<button className="btn btn-danger btn-wide" onClick={() => setShowModal(false)}>
						{t('cancel')}
					</button>
					<button className="btn btn-primary btn-wide" disabled={data?.products?.length === 0 || submitting} onClick={() => createReturn()}>
						{t('return')}
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default Return
